import {
  AnalyticsClient,
  AnalyticsStorage,
  clearAnalyticsSync,
  isIgnoredException,
} from '@ark/react';
import posthog from 'posthog-js';

const projectToken = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN;
const apiRoot = import.meta.env.VITE_API_ROOT;

export const isPostHogEnabled = Boolean(projectToken);

const backendHostname = apiRoot
  ? new URL(apiRoot).hostname
  : window.location.hostname;

const registerSuperProperties = () => {
  posthog.register({
    surface: 'manager-dashboard',
    environment: import.meta.env.VITE_ENVIRONMENT,
  });
};

if (projectToken) {
  posthog.init(projectToken, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com',
    ui_host: 'https://eu.posthog.com',
    defaults: '2026-08-30',
    person_profiles: 'identified_only',
    tracing_headers: [backendHostname],
    mask_all_text: true,
    autocapture: {
      css_selector_allowlist: ['[data-attr]'],
    },
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },
    before_send: event => (event && isIgnoredException(event) ? null : event),
    disable_session_recording: true,
    session_recording: {
      maskAllInputs: false,
      recordHeaders: false,
      recordBody: false,
    },
  });
  registerSuperProperties();
}

export const analyticsClient: AnalyticsClient = {
  capture: (event, properties) => posthog.capture(event, properties),
  identify: (distinctId, properties) =>
    posthog.identify(distinctId, properties),
  group: (groupType, groupKey, properties) =>
    posthog.group(groupType, groupKey, properties),
  setPersonProperties: properties => posthog.setPersonProperties(properties),
  resetGroups: () => posthog.resetGroups(),
  getDistinctId: () => posthog.get_distinct_id(),
};

export const analyticsStorage: AnalyticsStorage = {
  getItem: key => {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      return;
    }
  },
  removeItem: key => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      return;
    }
  },
};

export const capturePostHogException = (error: unknown) => {
  if (isPostHogEnabled) {
    posthog.captureException(error);
  }
};

export const startPostHogRecording = () => {
  if (isPostHogEnabled) {
    posthog.startSessionRecording();
  }
};

export const resetPostHog = () => {
  if (!isPostHogEnabled) {
    return;
  }
  posthog.stopSessionRecording();
  posthog.reset();
  void clearAnalyticsSync(analyticsStorage);
  registerSuperProperties();
};

export default posthog;
